import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import {
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
  Tabs,
  Tab,
} from "@mui/material";
import { BarChart, TrendingUp, People, Assessment } from "@mui/icons-material";
import { votingService } from "../services/votingService";
import { eventService } from "../services/eventService";
import { getEventSongsData } from "../data/songsData";

const ResultsPage = () => {
  const { eventKey } = useParams();
  const eventId = eventKey || "sanremo";
  const isEurovision = eventId === "eurovision";
  const { songs, categories, nights } = getEventSongsData(eventId);
  const [resultsRevealed, setResultsRevealed] = React.useState(!isEurovision);
  const [loadingEventConfig, setLoadingEventConfig] =
    React.useState(isEurovision);

  React.useEffect(() => {
    if (!isEurovision) {
      setResultsRevealed(true);
      setLoadingEventConfig(false);
      return;
    }
    setLoadingEventConfig(true);
    const unsubscribe = eventService.subscribeToEvent(
      eventId,
      (ev) => {
        setResultsRevealed(Boolean(ev?.resultsRevealed));
        setLoadingEventConfig(false);
      },
      () => {
        setResultsRevealed(false);
        setLoadingEventConfig(false);
      },
    );
    return () => unsubscribe();
  }, [eventId, isEurovision]);

  const canShowResults = !isEurovision || resultsRevealed;
  const {
    data: results,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["aggregatedResults", eventId],
    queryFn: async () => {
      console.log("React Query: Chiamando getAggregatedResults...");
      try {
        const result = await votingService.getAggregatedResults(eventId);
        console.log("React Query: getAggregatedResults successo:", result);
        return result;
      } catch (err) {
        console.error("React Query: getAggregatedResults errore:", err);
        throw err;
      }
    },
    refetchInterval: 30000, // Aggiorna ogni 30 secondi
    retry: 1,
    retryDelay: 1000,
    staleTime: 0, // Forza il refresh
    cacheTime: 0, // Non mantenere cache
    enabled: canShowResults,
  });

  const {
    data: allVotes,
    isLoading: votesLoading,
    error: votesError,
  } = useQuery({
    queryKey: ["allVotes", eventId],
    queryFn: async () => {
      console.log("React Query: Chiamando getAllVotes...");
      try {
        const result = await votingService.getAllVotes(eventId);
        console.log("React Query: getAllVotes successo:", result);
        return result;
      } catch (err) {
        console.error("React Query: getAllVotes errore:", err);
        throw err;
      }
    },
    refetchInterval: 30000,
    retry: 1,
    retryDelay: 1000,
    enabled: canShowResults,
  });

  const {
    data: liveStats,
    isLoading: isLoadingStats,
    error: liveStatsError,
  } = useQuery({
    queryKey: ["liveStats", eventId],
    queryFn: async () => {
      console.log("React Query: Chiamando getLiveStats...");
      try {
        const result = await votingService.getLiveStats(eventId);
        console.log("React Query: getLiveStats successo:", result);
        return result;
      } catch (err) {
        console.error("React Query: getLiveStats errore:", err);
        throw err;
      }
    },
    refetchInterval: 30000, // Aggiorna ogni 30 secondi
    retry: 1,
    retryDelay: 1000,
    staleTime: 0, // Forza il refresh
    cacheTime: 0, // Non mantenere cache
    enabled: canShowResults,
  });

  const [selectedTab, setSelectedTab] = React.useState(0);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const nightlyRankings = React.useMemo(() => {
    if (!allVotes) return {};

    const rankings = {};

    Object.values(nights).forEach((night) => {
      const nightVotes = allVotes.filter((v) => v.nightId === night.id);

      const songStats = {};
      const processedVotes = new Set(); // To track unique user-song votes

      // allVotes is already sorted by timestamp desc, so we encounter the latest vote first
      nightVotes.forEach((vote) => {
        const voteKey = `${vote.userId}_${vote.idSong}`;
        if (processedVotes.has(voteKey)) return; // Skip duplicates (older votes due to sort)
        processedVotes.add(voteKey);

        if (!songStats[vote.idSong]) {
          songStats[vote.idSong] = { total: 0, count: 0 };
        }

        let voteSum = 0;
        Object.values(vote.votes).forEach((val) => (voteSum += val));

        songStats[vote.idSong].total += voteSum;
        songStats[vote.idSong].count += 1;
      });

      const rankingList = Object.keys(songStats)
        .map((songId) => {
          const stats = songStats[songId];
          return {
            song: songId,
            average:
              stats.count > 0 ? (stats.total / stats.count).toFixed(2) : 0,
            total: stats.total,
            count: stats.count,
          };
        })
        .sort((a, b) => parseFloat(b.average) - parseFloat(a.average));

      rankings[night.id] = rankingList;
    });

    return rankings;
  }, [allVotes, nights]);

  if (loadingEventConfig) {
    return (
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!canShowResults) {
    return (
      <Box sx={{ p: 3, maxWidth: 900, mx: "auto" }}>
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom align="center">
            Risultati
          </Typography>
          <Alert severity="info">
            I risultati non sono ancora stati rivelati.
          </Alert>
        </Paper>
      </Box>
    );
  }

  if (isLoading || votesLoading || isLoadingStats) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || votesError || liveStatsError) {
    return (
      <Alert severity="error">
        Errore nel caricamento dei risultati:{" "}
        {error?.message || votesError?.message || liveStatsError?.message}
      </Alert>
    );
  }

  const getScoreColor = (score, max = 10) => {
    const percentage = score / max;
    if (percentage >= 0.8) return "#4caf50"; // Verde
    if (percentage >= 0.6) return "#ff9800"; // Arancione
    if (percentage >= 0.4) return "#f44336"; // Rosso
    return "#9e9e9e"; // Grigio
  };

  const getCategoryName = (categoryKey) => {
    const category = categories.find((c) => c.key === categoryKey);
    return category ? category.label : categoryKey;
  };

  const getSongName = (songKey) => {
    const song = songs.find((s) => s.key === songKey);
    return song ? `${song.artist} - ${song.title}` : songKey;
  };

  const topSong = results?.rankings?.overall?.[0];

  return (
    <Box>
      <Typography variant="h4" gutterBottom align="center">
        Risultati delle Votazioni
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="results tabs"
          centered
        >
          <Tab label="Generale" />
          {Object.values(nights).map((night) => (
            <Tab key={night.id} label={night.label} />
          ))}
        </Tabs>
      </Box>

      {selectedTab === 0 && (
        <>
          {/* Classifica Generale */}
          <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
            <Typography
              variant="h4"
              gutterBottom
              align="center"
              color="primary"
            >
              🏆 Classifica Generale Live
            </Typography>
            <Grid container spacing={2}>
              {results?.rankings?.overall?.slice(0, 10).map((item, index) => {
                const song = songs.find((s) => s.key === item.song);
                if (!song) return null;

                return (
                  <Grid item xs={12} sm={6} md={4} key={item.song}>
                    <Card
                      sx={{
                        height: "100%",
                        border:
                          index < 3
                            ? `3px solid ${index === 0 ? "#FFD700" : index === 1 ? "#C0C0C0" : "#CD7F32"}`
                            : "1px solid #e0e0e0",
                        position: "relative",
                      }}
                    >
                      {index < 3 && (
                        <Chip
                          label={`#${index + 1}`}
                          color={
                            index === 0
                              ? "warning"
                              : index === 1
                                ? "default"
                                : "error"
                          }
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            fontWeight: "bold",
                          }}
                        />
                      )}
                      <CardContent>
                        <Box display="flex" alignItems="center" mb={1}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            #{song.order}
                          </Typography>
                        </Box>

                        <Typography variant="h6" gutterBottom>
                          {song.artist}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          "{song.title}"
                        </Typography>

                        <Box
                          display="flex"
                          justifyContent="space-between"
                          mt={2}
                        >
                          <Box textAlign="center">
                            <Typography variant="h5" color="primary">
                              {parseFloat(item.average).toFixed(1)}
                            </Typography>
                            <Typography variant="caption">Media</Typography>
                          </Box>
                          <Box textAlign="center">
                            <Typography variant="h6" color="text.secondary">
                              #{index + 1}
                            </Typography>
                            <Typography variant="caption">Posizione</Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>

          {/* Classifiche per Categoria */}
          <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
            <Typography
              variant="h4"
              gutterBottom
              align="center"
              color="primary"
            >
              📊 Classifiche per Categoria
            </Typography>
            <Grid container spacing={3}>
              {Object.entries(results?.rankings?.byCategory || {}).map(
                ([category, ranking]) => (
                  <Grid item xs={12} md={6} lg={4} key={category}>
                    <Card variant="outlined" sx={{ height: "100%" }}>
                      <CardContent>
                        <Typography
                          variant="h6"
                          gutterBottom
                          color="primary"
                          align="center"
                        >
                          {getCategoryName(category)}
                        </Typography>
                        <Box>
                          {ranking.slice(0, 5).map((item, index) => {
                            const song = songs.find((s) => s.key === item.song);
                            if (!song) return null;

                            return (
                              <Box
                                key={item.song}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  mb: 1,
                                  px: 0,
                                }}
                              >
                                <Box
                                  sx={{
                                    bgcolor:
                                      index === 0
                                        ? "#FFD700"
                                        : getScoreColor(
                                            item.average || 0,
                                            categories.find(
                                              (c) => c.key === category,
                                            )?.max || 10,
                                          ),
                                    width: 24,
                                    height: 24,
                                    borderRadius: "50%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "white",
                                    fontSize: "0.75rem",
                                    fontWeight: "bold",
                                    mr: 2,
                                  }}
                                >
                                  {index + 1}
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontSize: "0.875rem" }}
                                  >
                                    {song.artist}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontSize: "0.75rem" }}
                                  >
                                    {song.title}
                                  </Typography>
                                </Box>
                                <Typography
                                  variant="body2"
                                  color="primary"
                                  fontWeight="bold"
                                >
                                  {parseFloat(item.average || 0).toFixed(1)}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ),
              )}
            </Grid>
          </Paper>

          {/* Statistiche Live */}
          <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
            <Typography
              variant="h4"
              gutterBottom
              align="center"
              color="primary"
            >
              📊 Statistiche Live
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <Box textAlign="center">
                  <People color="primary" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h3" color="primary">
                    {results?.totalVotes || 0}
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    Voti Totali
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={3}>
                <Box textAlign="center">
                  <TrendingUp color="success" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h3" color="success.main">
                    {topSong ? topSong.average.toFixed(1) : "0"}
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    Canzone Leader
                  </Typography>
                  {topSong && (
                    <Typography variant="body2" color="text.secondary">
                      {getSongName(topSong.song)}
                    </Typography>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} md={3}>
                <Box textAlign="center">
                  <Assessment color="info" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h3" color="info.main">
                    {liveStats?.userEngagement?.completionRate || "0"}%
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    Completamento Voti
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={3}>
                <Box textAlign="center">
                  <BarChart color="warning" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="h3" color="warning.main">
                    {liveStats?.votingTrend?.lastHour || 0}
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    Voti Ultima Ora
                  </Typography>
                  <Chip
                    label={
                      liveStats?.votingTrend?.trend === "increasing"
                        ? "In crescita"
                        : "Stabile"
                    }
                    color={
                      liveStats?.votingTrend?.trend === "increasing"
                        ? "success"
                        : "default"
                    }
                    size="small"
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* Statistiche Live */}
          <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
            <Typography
              variant="h4"
              gutterBottom
              align="center"
              color="primary"
            >
              📈 Popolarità delle Categorie
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ mb: 3 }}
            >
              Categorie con i punteggi medi più alti
            </Typography>
            <Grid container spacing={2}>
              {(liveStats?.categoryPopularity || []).map((data, index) => {
                const category = categories.find(
                  (c) => c.key === data.category,
                );
                const max = category?.max || 10;

                return (
                  <Grid item xs={12} sm={6} md={4} lg={2.4} key={data.category}>
                    <Card variant="outlined" sx={{ textAlign: "center", p: 2 }}>
                      <Typography variant="h6" color="primary" gutterBottom>
                        {getCategoryName(data.category)}
                      </Typography>
                      <Typography variant="h4" color="success.main">
                        {parseFloat(data.average || 0).toFixed(1)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Media generale
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={((data.average || 0) / max) * 100}
                        sx={{
                          mt: 1,
                          height: 6,
                          borderRadius: 3,
                          backgroundColor: "#e0e0e0",
                          "& .MuiLinearProgress-bar": {
                            backgroundColor: getScoreColor(
                              data.average || 0,
                              max,
                            ),
                          },
                        }}
                      />
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>

          {/* Ultimi Voti Ricevuti */}
          {allVotes && allVotes.length > 0 && (
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography
                variant="h4"
                gutterBottom
                align="center"
                color="primary"
              >
                ⏰ Ultimi Voti Ricevuti
              </Typography>

              <Grid container spacing={2}>
                {allVotes.slice(0, 10).map((vote, index) => (
                  <Grid item xs={12} md={6} key={vote.id}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom>
                          {vote.userIdentifier ||
                            vote.userEmail ||
                            "Utente Anonimo"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {vote.timestamp?.toDate?.()?.toLocaleString() ||
                            "Data non disponibile"}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 0.5, display: "block" }}
                        >
                          {Object.keys(vote.votes || {}).length} voti totali
                        </Typography>

                        <Box sx={{ mt: 1, maxHeight: 100, overflow: "auto" }}>
                          {Object.entries(vote.votes || {})
                            .filter(([voteKey]) => voteKey.includes("_"))
                            .slice(0, 10)
                            .map(([voteKey, score]) => {
                              const matchedCategory = categories.find((c) =>
                                voteKey.endsWith(`_${c.key}`),
                              );
                              if (!matchedCategory) return null;

                              const category = matchedCategory.key;
                              const prefix = voteKey.slice(
                                0,
                                -category.length - 1,
                              );
                              const songKey = prefix.split("_")[0];

                              const song = songs.find((s) => s.key === songKey);
                              return song ? (
                                <Chip
                                  key={voteKey}
                                  label={`${song.artist} (${getCategoryName(category)}): ${score}`}
                                  size="small"
                                  sx={{
                                    mr: 0.5,
                                    mb: 0.5,
                                    bgcolor: getScoreColor(
                                      score,
                                      matchedCategory.max || 10,
                                    ),
                                    color: "white",
                                    fontSize: "0.7rem",
                                  }}
                                />
                              ) : null;
                            })}
                          {Object.keys(vote.votes || {}).length > 10 && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              ... e altri{" "}
                              {Object.keys(vote.votes || {}).length - 10} voti
                            </Typography>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          )}
        </>
      )}

      {selectedTab > 0 && (
        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
          <Typography variant="h4" gutterBottom align="center" color="primary">
            🏆 Classifica {nights[selectedTab]?.label}
          </Typography>
          <Grid container spacing={2}>
            {nightlyRankings[selectedTab]?.map((item, index) => {
              const song = songs.find((s) => s.key === item.song);
              if (!song) return null;

              return (
                <Grid item xs={12} sm={6} md={4} key={item.song}>
                  <Card
                    sx={{
                      height: "100%",
                      border:
                        index < 3
                          ? `3px solid ${
                              index === 0
                                ? "#FFD700"
                                : index === 1
                                  ? "#C0C0C0"
                                  : "#CD7F32"
                            }`
                          : "1px solid #e0e0e0",
                      position: "relative",
                    }}
                  >
                    {index < 3 && (
                      <Chip
                        label={`#${index + 1}`}
                        color={
                          index === 0
                            ? "warning"
                            : index === 1
                              ? "default"
                              : "error"
                        }
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          fontWeight: "bold",
                        }}
                      />
                    )}
                    <CardContent>
                      <Box display="flex" alignItems="center" mb={1}>
                        <Typography variant="subtitle2" color="text.secondary">
                          #{song.order}
                        </Typography>
                      </Box>

                      <Typography variant="h6" gutterBottom>
                        {song.artist}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        "{song.title}"
                      </Typography>

                      <Box display="flex" justifyContent="space-between" mt={2}>
                        <Box textAlign="center">
                          <Typography variant="h5" color="primary">
                            {item.average}
                          </Typography>
                          <Typography variant="caption">Media</Typography>
                        </Box>
                        <Box textAlign="center">
                          <Typography variant="h6" color="text.secondary">
                            {item.total}
                          </Typography>
                          <Typography variant="caption">Totale</Typography>
                        </Box>
                        <Box textAlign="center">
                          <Typography variant="h6" color="text.secondary">
                            {item.count}
                          </Typography>
                          <Typography variant="caption">Voti</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
            {(!nightlyRankings[selectedTab] ||
              nightlyRankings[selectedTab].length === 0) && (
              <Grid item xs={12}>
                <Alert severity="info">
                  Nessun voto registrato per questa serata.
                </Alert>
              </Grid>
            )}
          </Grid>
        </Paper>
      )}
    </Box>
  );
};

export default ResultsPage;
